import type { AppError, BreathingSession } from '@/types'
import { EmptyState, ErrorState, Skeleton, StatusChip } from '@/components/ui'
import { BreathIcon } from '@/components/icons'
import { getExerciseById } from '../exercises'
import { formatDurationNl } from '../timing'

export type SessionHistoryProps = {
  sessions: BreathingSession[] | null
  loading: boolean
  error: AppError | null
  onRetry: () => void
}

const dateTimeFormat = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
})

function formatStartedAt(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : dateTimeFormat.format(date)
}

/** Recent breathing sessions with calm empty state and honest error state. */
export function SessionHistory({ sessions, loading, error, onRetry }: SessionHistoryProps) {
  return (
    <section className="breathing-history" aria-labelledby="breathing-history-title">
      <h2 id="breathing-history-title" className="breathing-history__title">
        Eerdere oefeningen
      </h2>

      {loading && <Skeleton lines={3} />}

      {!loading && error && (
        <ErrorState
          title="Geschiedenis niet beschikbaar"
          error={error}
          onRetry={onRetry}
        />
      )}

      {!loading && !error && sessions && sessions.length === 0 && (
        <EmptyState
          icon={<BreathIcon size={32} />}
          title="Nog geen oefeningen"
          body="Na je eerste oefening verschijnen je eerdere ademsessies hier."
        />
      )}

      {!loading && !error && sessions && sessions.length > 0 && (
        <ul className="breathing-history__list">
          {sessions.map((session) => {
            const exercise = getExerciseById(session.exerciseId)
            return (
              <li key={session.id} className="breathing-history__item">
                <div className="breathing-history__main">
                  <span className="breathing-history__name">
                    {exercise ? exercise.name : 'Ademoefening'}
                  </span>
                  <span className="breathing-history__meta">
                    {formatStartedAt(session.startedAt)}
                    {' · '}
                    {formatDurationNl(session.durationSeconds)}
                  </span>
                </div>
                <StatusChip status={session.completed ? 'success' : 'neutral'}>
                  {session.completed ? 'Afgerond' : 'Gestopt'}
                </StatusChip>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
